import { gql } from '@apollo/client';
import client from 'graphql/client';
import accessFragment from 'redux/job/queries';

const companyInviteFragment = gql`
  fragment CompanyInvite on CompanyInvite {
    id
    email
    status
    expiresAt
    permissions {
      job
      profile
      invoice
      candidateSearch
    }
    invitedBy {
      id
      name
    }
  }
`;

/* fetch teams */
const fetchTeamGraphql = (payload) => {
  const params = {};
  const { variables } = payload;

  if (payload.recache) {
    params.fetchPolicy = 'network-only';
  }

  // companyInvites is resolved through identity; if that one field fails, keep the rest of the
  // team instead of rejecting the whole query (the page then just shows no invite rows).
  params.errorPolicy = 'all';

  const query = gql`
    query fetchTeam {
      team {
        invitedMembers {
          id
          allowedContent
          allowedSection
          email
          validUntil
          issuedDate
          contentDescription
          redeemed
        }
        companyInvites {
          ...CompanyInvite
        }
        teamMembers {
          id
          access {
            id
            allowed
            contentType
            lastChange {
              timeOfChange
              employer {
                id
                name
                email
              }
            }
          }
          employer {
            id
            email
            name
            initials
            currentUser
            photoThumbnail
          }
        }
      }
    }
    ${companyInviteFragment}
  `;
  return client.query({
    query,
    variables,
    ...params,
  });
};

/* revoke access */
const mutateRevokeAccessGraphql = (variables) => {
  const params = {};

  const mutation = gql`
    mutation revokeProjectAccess($id: Int!) {
      revokeProjectAccess(id: $id) {
        id
        access {
          ...Access
        }
      }
    }
    ${accessFragment}
  `;
  return client.mutate({
    mutation,
    variables,
    ...params,
  });
};

/* grant access */
const mutateGrantAccessGraphql = (variables) => {
  const params = {};

  const mutation = gql`
    mutation addProjectAccess($email: String!, $allowedContent: Int, $allowedSection: String) {
      addProjectAccess(
        email: $email
        allowedContent: $allowedContent
        allowedSection: $allowedSection
      ) {
        id
        access {
          ...Access
        }
      }
    }
    ${accessFragment}
  `;
  return client.mutate({
    mutation,
    variables,
    ...params,
  });
};

const mutateEmployerAccessGraphql = (variables) => {
  const params = {};
  const mutation = gql`
    mutation modifyEmployerAccess(
      $employerId: Int!
      $admin: Boolean!
      $job: Boolean!
      $profile: Boolean!
      $invoice: Boolean!
      $candidate_search: Boolean
    ) {
      modifyEmployerAccess(
        employerId: $employerId
        admin: $admin
        job: $job
        profile: $profile
        invoice: $invoice
        candidateSearch: $candidate_search
      ) {
        id
        access {
          id
          allowed
          contentType
          lastChange {
            timeOfChange
            employer {
              id
              name
              email
            }
          }
        }
        employer {
          id
          email
          name
          initials
          photoThumbnail
        }
      }
    }
  `;
  return client.mutate({
    mutation,
    variables,
    ...params,
  });
};

const mutateRedeemAccessGraphql = (token) => {
  const mutation = gql`
    mutation redeemAccess($token: String!) {
      redeemAccess(token: $token) {
        id
        allowedContent
      }
    }
  `;

  return client.mutate({
    mutation,
    variables: {
      token,
    },
  });
};

/* invite a new user into the company */
const mutateInviteTeamMemberGraphql = (variables) =>
  client.mutate({
    mutation: gql`
      mutation inviteTeamMember(
        $email: String!
        $job: Boolean
        $profile: Boolean
        $invoice: Boolean
        $candidateSearch: Boolean
      ) {
        inviteTeamMember(
          email: $email
          job: $job
          profile: $profile
          invoice: $invoice
          candidateSearch: $candidateSearch
        ) {
          ...CompanyInvite
        }
      }
      ${companyInviteFragment}
    `,
    variables,
  });

const mutateResendTeamInviteGraphql = (id) =>
  client.mutate({
    mutation: gql`
      mutation resendTeamInvite($id: Int!) {
        resendTeamInvite(id: $id) {
          ...CompanyInvite
        }
      }
      ${companyInviteFragment}
    `,
    variables: { id },
  });

const mutateRevokeTeamInviteGraphql = (id) =>
  client.mutate({
    mutation: gql`
      mutation revokeTeamInvite($id: Int!) {
        revokeTeamInvite(id: $id) {
          ...CompanyInvite
        }
      }
      ${companyInviteFragment}
    `,
    variables: { id },
  });

export {
  fetchTeamGraphql,
  mutateInviteTeamMemberGraphql,
  mutateResendTeamInviteGraphql,
  mutateRevokeTeamInviteGraphql,
  mutateEmployerAccessGraphql,
  mutateGrantAccessGraphql,
  mutateRedeemAccessGraphql,
  mutateRevokeAccessGraphql,
};
